/**
 * RBAC-5.3 · NFR-6b. The Roles & Permissions blocked surface.
 *
 * ⚠️ IT MUST NOT BE A BARE REFUSAL. It names what happened and offers a route out, and the route
 * depends on the account: when someone on it administers roles, it names them; when nobody does,
 * "ask an Admin" would send the reader looking for a person who does not exist, so it says so and
 * offers to ask Joules support instead (`request_account_recovery`). The words are
 * `rolesBlockedCopy()` in `lib/accountRecovery.ts`, pinned by its tests.
 *
 * While the answer is unknown, or could not be read, it shows the generic sentence, which claims
 * nothing it does not know.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { CheckCircle2, LifeBuoy, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useTenant } from '@/contexts/TenantContext';
import { SUPPORT_TICKETS_PATH, requestFiledCopy, rolesBlockedCopy } from '@/lib/accountRecovery';
import {
  fetchMyAccountAdministration,
  recoveryErrorText,
  requestAccountRecovery,
} from '@/services/accountRecoveryService';

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, 'd MMM yyyy');
};

export function RolesNoAccess() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id ?? null;
  const queryClient = useQueryClient();
  const queryKey = ['my-account-administration', tenantId];
  const [note, setNote] = useState('');

  const { data: state } = useQuery({
    queryKey,
    queryFn: fetchMyAccountAdministration,
    enabled: tenantId !== null,
    staleTime: 60_000,
    retry: false,
  });

  const request = useMutation({
    mutationFn: () => requestAccountRecovery(note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const copy = rolesBlockedCopy(state ?? null);
  const filedAt = state?.openRequestAt ?? (request.isSuccess ? request.data.createdAt : null);
  const filed = Boolean(state?.openRequestAt) || request.isSuccess;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center" data-testid="roles-no-access">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        {copy.offerRequest || filed ? (
          <LifeBuoy className="h-6 w-6 text-muted-foreground" />
        ) : (
          <Lock className="h-6 w-6 text-muted-foreground" />
        )}
      </div>
      <h2 className="mt-4 text-lg font-semibold text-foreground">{copy.title}</h2>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{copy.body}</p>

      {filed ? (
        <p
          className="mt-4 flex max-w-md items-start gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-left text-sm text-foreground"
          role="status"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>{requestFiledCopy(filedAt, formatDate)}</span>
        </p>
      ) : copy.offerRequest ? (
        <form
          className="mt-5 w-full max-w-md space-y-2 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            request.mutate();
          }}
        >
          <label htmlFor="recovery-note" className="text-sm font-medium text-foreground">
            Anything Joules support should know? <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <Textarea
            id="recovery-note"
            value={note}
            maxLength={1000}
            rows={3}
            onChange={(e) => setNote(e.target.value)}
            placeholder="For example, who used to be your admin, or who should be one now."
          />
          {request.isError && (
            <p className="text-sm text-destructive" role="alert">
              {recoveryErrorText(request.error)}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button type="submit" disabled={request.isPending}>
              {request.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ask Joules to restore administration
            </Button>
            <Link to={SUPPORT_TICKETS_PATH} className="text-sm text-primary underline-offset-4 hover:underline">
              Or open a support ticket
            </Link>
          </div>
        </form>
      ) : null}
    </div>
  );
}
